/**
 * Dietitian Profile Form Component (Step 2)
 *
 * Professional profile form for dietitian registration workflow.
 * Includes state/country selection, areas of practice multi-select,
 * phone input, APD number validation, and optional photo upload.
 *
 * @module components/dietician-registration/DieticianProfileForm
 */

import React from 'react';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { CountryPhoneSelector } from '@/components/ui/CountryPhoneSelector';
import { ProfilePhotoUpload } from '@/components/ui/ProfilePhotoUpload';
import { useTheme } from '@/hooks/useTheme';
import {
  useDieticianProfileFormState,
  type DieticianProfileFormData,
  type UseDieticianProfileFormStateReturn,
} from '@/hooks/useDieticianProfileFormState';
import { cn } from '@/utils/cn';
import { AUSTRALIAN_STATE_OPTIONS, COUNTRY_OPTIONS } from '@/constants/geographic';
import { AreasOfPracticeSelector, LanguagesSelector } from '../ui';
import { LanguageCode } from '@/types/languageCodes';
import { FeesAndPaymentSection } from './FeesAndPaymentSection';
import { mapServerFieldErrors } from '@/utils/feePolicy';


/**
 * Component props interface
 */
export interface DieticianProfileFormProps {
  onSubmit: (data: DieticianProfileFormData) => Promise<void>;
  initialData?: Partial<DieticianProfileFormData>;
  isLoading: boolean;
  errors: Record<string, string>;
  mode?: 'registration' | 'edit';
  onCancel?: () => void;
}

/**
 * DietitianProfileForm Component
 *
 * Step 2 of the dietitian registration workflow.
 * Collects professional profile information including location,
 * areas of practice, contact details, and credentials.
 */
export const DieticianProfileForm: React.FC<DieticianProfileFormProps> = ({
  onSubmit,
  initialData,
  isLoading,
  errors,
  mode = 'registration',
  onCancel,
}) => {
  const { isDark } = useTheme();

  // Use the custom hook for form state and logic
  const formState = useDieticianProfileFormState({
    initialData,
    onSubmit,
  }) as UseDieticianProfileFormStateReturn & {
    handleLanguagesChange: (languages: LanguageCode[]) => void;
  };

  const {
    formData,
    fieldErrors,
    fieldRefs,
    handleFieldChange,
    handleFieldBlur,
    handlePhoneChange,
    handleAreasOfPracticeChange,
    handleLanguagesChange,
    handlePhotoChange,
    handlePracticeAddressChange,
    handleFeePolicyChange,
    resetFeePolicy,
    handleSubmit,
  } = formState;

  // The form translates the server's payment_policy.<field> keys (design §4.13.4, D20): per-field display is its concern
  const feePolicyErrors = Object.fromEntries(
    Object.entries({ ...fieldErrors, ...mapServerFieldErrors(errors) }).filter(([key, value]) => key.startsWith('feePolicy.') && value)
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-6" role="form">
      {/* Form error display */}
      {errors.form && (
        <div className={cn(
          'p-4 rounded-lg border',
          isDark
            ? 'bg-red-900/20 border-red-400 text-red-300'
            : 'bg-red-50 border-red-200 text-red-800'
        )} data-testid="form-error">
          <p className="text-sm font-medium">{errors.form}</p>
        </div>
      )}

      {/* Personal Information Section */}
      <div className="space-y-4">
        <h3 className={cn(
          'text-lg font-semibold',
          isDark ? 'text-white' : 'text-gray-900'
        )}>
          Personal Information
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="First Name"
            required
            error={fieldErrors.firstName}
          >
            <Input
              ref={(el) => { fieldRefs.current.firstName = el; }}
              type="text"
              value={formData.firstName}
              onChange={(e) => handleFieldChange('firstName', e.target.value)}
              onBlur={() => handleFieldBlur('firstName')}
              placeholder="Enter your first name"
              disabled={isLoading}
              autoComplete="given-name"
              aria-required="true"
            />
          </FormField>

          <FormField
            label="Last Name"
            required
            error={fieldErrors.lastName}
          >
            <Input
              ref={(el) => { fieldRefs.current.lastName = el; }}
              type="text"
              value={formData.lastName}
              onChange={(e) => handleFieldChange('lastName', e.target.value)}
              onBlur={() => handleFieldBlur('lastName')}
              placeholder="Enter your last name"
              disabled={isLoading}
              autoComplete="family-name"
              aria-required="true"
            />
          </FormField>
        </div>
      </div>


      {/* Practice Address Section */}
      <div className="space-y-4">
        <h3 className={cn(
          'text-lg font-semibold',
          isDark ? 'text-white' : 'text-gray-900'
        )}>
          Practice Address
        </h3>
        <p className={cn(
          'text-sm',
          isDark ? 'text-secondary-400' : 'text-secondary-600'
        )}>
          Provide your practice address for patient reference
        </p>

        <div className="space-y-4">
          {/* Street Address */}
          <FormField
            label="Street Address"
            required
            error={fieldErrors['practiceAddress.streetAddress']}
          >
            <Input
              ref={(el) => { fieldRefs.current['practiceAddress.streetAddress'] = el; }}
              type="text"
              value={formData.practiceAddress.streetAddress}
              onChange={(e) => handlePracticeAddressChange('streetAddress', e.target.value)}
              onBlur={() => handleFieldBlur('practiceAddress.streetAddress')}
              placeholder="Enter street address"
              disabled={isLoading}
              state={fieldErrors['practiceAddress.streetAddress'] ? 'error' : 'default'}
            />
          </FormField>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* City */}
            <FormField
              label="City"
              required
              error={fieldErrors['practiceAddress.city']}
            >
              <Input
                ref={(el) => { fieldRefs.current['practiceAddress.city'] = el; }}
                type="text"
                value={formData.practiceAddress.city}
                onChange={(e) => handlePracticeAddressChange('city', e.target.value)}
                onBlur={() => handleFieldBlur('practiceAddress.city')}
                placeholder="Enter city"
                disabled={isLoading}
                state={fieldErrors['practiceAddress.city'] ? 'error' : 'default'}
              />
            </FormField>

            {/* State */}
            <FormField
              label="State"
              required
              error={fieldErrors['practiceAddress.state']}
            >
              <Select
                ref={(el) => { fieldRefs.current['practiceAddress.state'] = el; }}
                value={formData.practiceAddress.state}
                onValueChange={(value) => handlePracticeAddressChange('state', value)}
                onBlur={() => handleFieldBlur('practiceAddress.state')}
                disabled={isLoading}
                state={fieldErrors['practiceAddress.state'] ? 'error' : 'default'}
                options={[
                  { value: '', label: 'Select state', disabled: false },
                  ...AUSTRALIAN_STATE_OPTIONS.slice(1).map(option => ({
                    value: option.value,
                    label: option.label,
                    disabled: false
                  }))
                ]}
              >
              </Select>
            </FormField>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Postal Code */}
            <FormField
              label="Postal Code"
              required
              error={fieldErrors['practiceAddress.postalCode']}
            >
              <Input
                ref={(el) => { fieldRefs.current['practiceAddress.postalCode'] = el; }}
                type="text"
                value={formData.practiceAddress.postalCode}
                onChange={(e) => handlePracticeAddressChange('postalCode', e.target.value)}
                onBlur={() => handleFieldBlur('practiceAddress.postalCode')}
                placeholder="Enter postal code"
                maxLength={4}
                disabled={isLoading}
                autoComplete="postal-code"
                state={fieldErrors['practiceAddress.postalCode'] ? 'error' : 'default'}
              />
            </FormField>

            {/* Country */}
            <FormField
              label="Country"
              required
              error={fieldErrors['practiceAddress.country']}
            >
              <Select
                ref={(el) => { fieldRefs.current['practiceAddress.country'] = el; }}
                value={formData.practiceAddress.country}
                onValueChange={(value) => handlePracticeAddressChange('country', value)}
                onBlur={() => handleFieldBlur('practiceAddress.country')}
                disabled={isLoading}
                state={fieldErrors['practiceAddress.country'] ? 'error' : 'default'}
                options={COUNTRY_OPTIONS.map(option => ({
                  value: option.value,
                  label: option.label,
                  disabled: false
                }))}
              >
              </Select>
            </FormField>
          </div>
        </div>
      </div>

      {/* Professional Information Section */}
      <div className="space-y-4">
        <h3 className={cn(
          'text-lg font-semibold',
          isDark ? 'text-white' : 'text-gray-900'
        )}>
          Professional Information
        </h3>

        {/* Areas of Practice */}
        <FormField
          label="Areas of Practice"
          required
          error={fieldErrors.areasOfPractice}
          helperText="Select all areas where you provide dietetic services"
        >
          <AreasOfPracticeSelector
                        ref={(el) => { fieldRefs.current.areasOfPractice = el as HTMLInputElement | HTMLDivElement | null; }}
                        selectedConditions={formData.areasOfPractice}
                        onConditionsChange={handleAreasOfPracticeChange}
                        error={fieldErrors.areasOfPractice}
                        disabled={isLoading}
                      />
        </FormField>

        {/* Languages Spoken */}
        <FormField
          label="Languages Spoken"
          required
          error={fieldErrors.languagesSpoken}
          helperText="Select all languages you can confidently support during consultations"
        >
          <LanguagesSelector
            ref={(el) => { fieldRefs.current.languagesSpoken = el as HTMLInputElement | HTMLDivElement | null; }}
            selectedLanguages={formData.languagesSpoken}
            onLanguagesChange={handleLanguagesChange}
            error={fieldErrors.languagesSpoken}
            disabled={isLoading}
          />
        </FormField>

        {/* APD Number */}
        <FormField
          label="APD Number"
          required
          error={fieldErrors.apdNumber}
          helperText="Your Accredited Practicing Dietitian number (e.g., APD123456)"
        >
          <Input
            ref={(el) => { fieldRefs.current.apdNumber = el; }}
            type="text"
            value={formData.apdNumber}
            onChange={(e) => handleFieldChange('apdNumber', e.target.value)}
            onBlur={() => handleFieldBlur('apdNumber')}
            placeholder="Enter APD Number"
            disabled={isLoading}
            state={fieldErrors.apdNumber ? 'error' : 'default'}
            required
          />
        </FormField>
      </div>

      {/* Fees and payment (design-patient-payment-guidance §4.13.4) */}
      <FeesAndPaymentSection
        mode={mode}
        fields={formData.feePolicy}
        errors={feePolicyErrors}
        disabled={isLoading}
        onChange={handleFeePolicyChange}
        onBlur={(field) => handleFieldBlur(`feePolicy.${field}`)}
        onReset={resetFeePolicy}
      />

      {/* Contact Information Section */}
      <div className="space-y-4">
        <h3 className={cn(
          'text-lg font-semibold',
          isDark ? 'text-white' : 'text-gray-900'
        )}>
          Contact Information
        </h3>

        {/* Phone Number */}
        <FormField
          label="Phone Number"
          required
          error={fieldErrors.phone}
          helperText="Your professional contact number"
        >
          <div ref={(el) => { fieldRefs.current.phone = el; }}>
            <CountryPhoneSelector
              value={formData.phone}
              onChange={handlePhoneChange}
              disabled={isLoading}
              error={fieldErrors.phone}
              required
            />
          </div>
        </FormField>
      </div>

      {/* Profile Photo Section */}
      <div className="space-y-4">
        <h3 className={cn(
          'text-lg font-semibold',
          isDark ? 'text-white' : 'text-gray-900'
        )}>
          Profile Photo
        </h3>

        <FormField
          label="Profile Photo"
          error={fieldErrors.photo}
          helperText="Optional: Upload a professional photo for your profile"
        >
          <ProfilePhotoUpload
            value={formData.photo ? URL.createObjectURL(formData.photo) : undefined}
            onChange={(photoData) => {
              if (photoData) {
                // Convert base64 data URL to File object
                fetch(photoData)
                  .then(res => res.blob())
                  .then(blob => {
                    const file = new File([blob], 'profile-photo.jpg', { type: 'image/jpeg' });
                    handlePhotoChange(file);
                  })
                  .catch(() => handlePhotoChange(undefined));
              } else {
                handlePhotoChange(undefined);
              }
            }}
            disabled={isLoading}
            error={fieldErrors.photo}
          />
        </FormField>
      </div>

      {/* Submit Button */}
      <div className={`flex ${mode === 'edit' ? 'justify-between' : 'justify-end'} pt-6 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`} data-testid="button-container">
        {mode === 'edit' && onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isLoading}
            className="min-w-[120px]"
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          disabled={isLoading}
          className="min-w-[120px]"
        >
          {isLoading ? (
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Saving...</span>
            </div>
          ) : (
            mode === 'edit' ? 'Save Changes' : 'Continue'
          )}
        </Button>
      </div>

      {/* Form Summary */}
      <div className={cn(
        'text-sm',
        isDark ? 'text-secondary-400' : 'text-secondary-600'
      )}>
        <p>
          By continuing, you confirm that the information provided is accurate and up-to-date.
          This information will be used to verify your professional credentials.
        </p>
      </div>
    </form>
  );
};

export default DieticianProfileForm;